import React from "react";

import { SkeletonList } from "~/components/skeleton/list";
import { Tabs } from "~/components/tabs";
import { TabsContent } from "~/components/tabs/content";
import { TabsList } from "~/components/tabs/list";
import { TabsTrigger } from "~/components/tabs/trigger";
import { useScoped } from "~/tools/impulse";

import { ScrollingContainer } from "../../../../components/_scrolling-container";
import { CloseDrawerButton, ExplorationDrawer } from "../../../../components/react/drawer/ExplorationDrawer";
import { ErrorScreen } from "../../../../components/react/full-center-view-layout/error-screen";
import { queryFullComponent } from "../../../../queries";
import { useQuery } from "../../../../queries/react-query";

import { DrawerTitle } from "./_drawer-title";
import { ResourceTab } from "./_resource-tab";
import { ResourceTable } from "./_resource-table";
import { TopologyLink } from "./_topology-link";
import type { RelatedResourcesDrawerState } from "./state";

export const RelatedResourcesDrawer: React.FC<{
	when: undefined | RelatedResourcesDrawerState;
}> = ({ when }) => (
	<ExplorationDrawer
		className="p-0!"
		footer={(state) => (
			<>
				<CloseDrawerButton />
				<TopologyLink {...state} />
			</>
		)}
		headerTitle={(state) => <DrawerTitle {...state} />}
		scrollX="hidden"
		scrollY="hidden"
		when={when}
	>
		{(state) => <Content {...state} />}
	</ExplorationDrawer>
);

const Content: React.FC<RelatedResourcesDrawerState> = ({
	timestamp,
	timeRange,
	componentIdOrIdentifier,
	activeTabTitle,
}) => {
	const activeTitle = useScoped(activeTabTitle);
	const { error, data: fullComponent } = useQuery({
		refetchOnMount: false,
		refetchOnReconnect: false,
		refetchOnWindowFocus: false,

		...queryFullComponent({ componentIdOrIdentifier, timestamp }),
	});

	if (error != null) {
		return <ErrorScreen error={error} noShadow />;
	}

	return (
		<Tabs
			asChild
			className="flex min-h-full flex-col"
			onValueChange={(value) => activeTabTitle.setValue(value)}
			value={activeTitle}
		>
			<ScrollingContainer className="size-full min-w-180 px-4" scrollX="auto">
				<TabsList className="sticky top-0 left-0 z-5 w-full justify-start bg-gray-100 py-4">
					<SkeletonList count={1} each={fullComponent?.relatedResources}>
						{(resource) => (
							<TabsTrigger
								asChild
								disabled={resource == null}
								key={resource?.title ?? activeTitle}
								value={resource?.title ?? activeTitle}
							>
								<ResourceTab
									className="data-[count=0]:hidden"
									presentationIdentifier={resource?.presentationIdentifier}
									stql={resource?.stql}
									timestamp={timestamp}
									title={resource?.title}
								/>
							</TabsTrigger>
						)}
					</SkeletonList>
				</TabsList>

				<SkeletonList count={1} each={fullComponent?.relatedResources}>
					{(resource) => (
						<TabsContent className="flex-1" key={resource?.title ?? activeTitle} value={resource?.title ?? activeTitle}>
							<ResourceTable
								presentationIdentifier={resource?.presentationIdentifier}
								stql={resource?.stql}
								timeRange={timeRange}
								timestamp={timestamp}
							/>
						</TabsContent>
					)}
				</SkeletonList>
			</ScrollingContainer>
		</Tabs>
	);
};
